import { ACTION_ROLL } from '../../character/content/templates';

export default function FlowOfGame() {
  return (
    <>
      <p>Daggerheart is a conversation. The GM describes fictional scenarios involving the PCs, and the players take turns describing how their characters react. The goal of every person at the table is to build upon everyone else's ideas and collaboratively tell a satisfying story. The system facilitates this collaborative process by providing structure to the conversation and mechanics for resolving moments of tension where fate or fortune determine the outcome of events.</p>

      <h3 id="player-principles">PLAYER PRINCIPLES &amp; BEST PRACTICES</h3>
      <p>To get the most out of Daggerheart, we recommend players keep the following principles and practices in mind throughout each session:</p>

      <h3>PRINCIPLES</h3>
      <ul>
        <li>Be a fan of your character and their journey</li>
        <li>Spotlight your friends</li>
        <li>Address the characters and address the players</li>
        <li>Build the world together</li>
        <li>Play to find out what happens</li>
        <li>Hold on gently</li>
      </ul>

      <h3>BEST PRACTICES</h3>
      <ul>
        <li>Embrace danger</li>
        <li>Use your resources</li>
        <li>Tell the story</li>
        <li>Discover your character</li>
      </ul>

      <h3 id="core-gameplay-loop">CORE GAMEPLAY LOOP</h3>
      <p>The <strong>core gameplay loop</strong> is the procedure that drives every scene, both in and out of combat:</p>

      <h3>STEP 1:</h3>
      <h4><strong>SET THE SCENE</strong></h4>
      <p>The GM describes a scenario, establishing the PCs' surroundings and any dangers, NPCs, or other important details the characters would notice.</p>

      <h3>STEP 2</h3>
      <h4><strong>ASK AND ANSWER QUESTIONS</strong></h4>
      <p>The players ask clarifying questions to explore the scene more deeply and gather information that could inform their characters' actions. The GM responds to these questions by giving the players information their characters could easily obtain, or by asking questions of their own to the players. The players also respond to any questions the GM poses to them. In this way, the table builds out the fiction collaboratively.</p>

      <h3>STEP 3</h3>
      <h4><strong>BUILD ON THE FICTION</strong></h4>
      <p>As the scene develops, the players find opportunities to take action—problems to solve, obstacles to overcome, mysteries to investigate, and so on. The players describe how their characters proceed; if their proposed actions carry no chance of failure (or if failure would be boring), they automatically succeed. But if the outcome of their action is unknown, the GM calls for an {ACTION_ROLL}. Either way, the table works the outcome into the story and moves the fiction forward, narrating how the PC's actions have changed things.</p>

      <h3>STEP 4</h3>
      <h4><strong>GO BACK TO STEP 1</strong></h4>
      <p>The process repeats from the beginning, with the GM relaying any updated details or material changes to the players. This process continues until the end of the scene is triggered by a mechanic or arrives organically.</p>

      <h2 id="the-spotlight">THE SPOTLIGHT</h2>
      <p>The <strong>spotlight</strong> is a symbol that represents the table's attention—and therefore the immediate focus of both the narrative and the game mechanics. Any time a character or player becomes the focus of a scene, they they “are in the spotlight” or “have the spotlight.”</p>
      <p>The spotlight moves around the table organically as scenes unfold unless a mechanical trigger determines where the spotlight goes next. For example, when a player fails an action roll, the mechanics prompt the GM to seize the spotlight and make a GM move.</p>

      <h2 id="turn-order-action-economy">TURN ORDER &amp; ACTION ECONOMY</h2>
      <p>Daggerheart's turns don't follow a traditional, rigid format: there is no explicit initiative mechanic and characters don't have a set number of actions they can take or things they can do before the spotlight passes to someone else. A player with the spotlight describes what their character does and the spotlight simply swings to whoever:</p>
      <ul>
        <li>the fiction would naturally turn it toward</li>
        <li>hasn't had the focus in a while, or</li>
        <li>a triggered mechanic puts it on</li>
      </ul>
      <h3><strong>Optional: Spotlight Tracker Tool</strong></h3>
      <p>If your group prefers a more traditional action economy, you can use tokens to track how many times a player has had the spotlight: At the start of a session or scene, each player adds a certain number of tokens (we recommend 3) to their character sheet and removes a token each time they take an action. If the spotlight would swing to someone without any tokens, it swings to someone else instead. Once every player has used all their available tokens, players refill their character sheet with the same number of tokens as before, then continue playing.</p>
    </>
  );
}
